import { ApiError, RateLimitError, UnauthenticatedError } from "./api.js";

/**
 * GT-UI-API-RATE-LIMIT-REQUEST-STORM-FIX-02 — QueryClient retry policy.
 *
 * Phase-0 found the web/admin clients had `retry: 1` with NO backoff, so
 * a 429 was retried immediately — adding traffic to an already
 * over-budget window. This policy makes a 429 *reduce* traffic:
 *
 *   - 401 → never retried (the cache redirects to login).
 *   - 429 → retried at most once, and ONLY after the server's
 *     `Retry-After` window, and ONLY when that window is short. A long
 *     window fails fast so the UI can show a controlled "try again in
 *     N s" message instead of hanging the query (and instead of
 *     retrying into a still-closed window, which just burns a request).
 *   - other 4xx (403/404/409/422 …) → deterministic, never retried.
 *   - network / 5xx → one retry with exponential backoff (legacy
 *     behaviour preserved).
 *
 * The functions are pure and injectable so the policy is unit-tested
 * without React Query (`rateLimitRetry.test.ts`).
 */

/** Max auto-retries for a transient (network / 5xx) failure. */
export const TRANSIENT_MAX_RETRIES = 1;
/** Max auto-retries for a 429 (each gated behind its Retry-After). */
export const RATE_LIMIT_MAX_RETRIES = 1;
/**
 * Only auto-retry a 429 when the advertised wait is at most this many
 * seconds. Longer waits fail fast → the UI surfaces a controlled
 * message rather than spinning for a minute.
 */
export const RATE_LIMIT_AUTO_RETRY_MAX_SECONDS = 15;
/** Hard ceiling on any single auto-retry delay (ms). */
export const MAX_RETRY_DELAY_MS = 15_000;

/**
 * `retry` predicate for `QueryClient` default options. `random` is
 * injectable for deterministic tests; production uses `Math.random`.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof UnauthenticatedError) return false;
  if (error instanceof RateLimitError) {
    if (failureCount >= RATE_LIMIT_MAX_RETRIES) return false;
    const wait = error.retryAfterSeconds;
    // Retry only when there is no advertised wait, or it is short.
    return wait == null || wait <= RATE_LIMIT_AUTO_RETRY_MAX_SECONDS;
  }
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < TRANSIENT_MAX_RETRIES;
}

/**
 * `retryDelay` for `QueryClient` default options — milliseconds before
 * the next attempt. Honors `Retry-After` for 429s; exponential backoff
 * otherwise. A ±20% jitter de-synchronises a fleet of queries that all
 * 429'd together so they do not retry on the same tick (stampede).
 */
export function retryDelayForQuery(
  failureCount: number,
  error: unknown,
  random: () => number = Math.random,
): number {
  if (error instanceof RateLimitError && error.retryAfterSeconds != null) {
    const base = Math.min(error.retryAfterSeconds * 1000, MAX_RETRY_DELAY_MS);
    return base + base * 0.2 * random();
  }
  const base = Math.min(1000 * 2 ** failureCount, MAX_RETRY_DELAY_MS);
  return base + base * 0.2 * random();
}

/** True when the error is an HTTP 429 (rate limited). */
export function isRateLimitError(error: unknown): error is RateLimitError {
  return error instanceof RateLimitError;
}
